import { Head, Link, router } from "@inertiajs/react"
import {
  Plus,
  TrendingUp,
  Target,
  DollarSign,
  BarChart3,
  Zap,
} from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Progress } from "@/components/ui/progress"
import { InfoTooltip } from "@/components/info-tooltip"
import { AppLayout } from "@/layouts/app-layout"

interface FiMetrics {
  annual_expenses: number
  fi_number: number
  current_portfolio_value: number
  total_liabilities: number
  net_worth: number
  fi_progress_percentage: number
}

interface RecentExpense {
  id: number
  amount: number
  category: string
  description: string
  expense_date: string
}

interface ActiveGoal {
  id: number
  target_amount: number
  current_amount: number
  goal_type: string
  progress_percentage: number
  amount_remaining: number
  goal_type_human: string
}

interface PortfolioSummary {
  id: number
  name: string
  total_value: number
  total_cost_basis: number
  total_return: number
  total_return_percentage: number
  portfolio_positions: {
    id: number
    symbol: string
    shares: number
    current_price: number
    current_value: number
    unrealized_gain_loss_percentage: number
  }[]
}

interface DemoUser {
  id: number
  name: string
  email: string
  portfolios_count: number
  expenses_count: number
  goals_count: number
  total_portfolio_value: number
}

interface DashboardProps {
  fi_metrics: FiMetrics
  recent_expenses: RecentExpense[]
  active_goals: ActiveGoal[]
  portfolio_summary: PortfolioSummary[]
  demo_users: DemoUser[]
}

export default function DashboardIndex({
  fi_metrics,
  recent_expenses,
  active_goals,
  portfolio_summary,
  demo_users,
}: DashboardProps) {
  const formatCurrency = (amount: number) => {
    if (!amount || isNaN(amount)) {
      return "$0.00"
    }
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  const getReturnColor = (return_percentage: number) => {
    if (return_percentage > 0) return "text-green-600"
    if (return_percentage < 0) return "text-red-600"
    return "text-gray-600"
  }

  const handleAssumeUser = (userId: number) => {
    router.post(`/users/${userId}/assume_user`)
  }

  const totalPortfolioValue = portfolio_summary.reduce(
    (sum, portfolio) => sum + portfolio.total_value,
    0,
  )
  const totalPortfolioReturn = portfolio_summary.reduce(
    (sum, portfolio) => sum + portfolio.total_return,
    0,
  )

  return (
    <AppLayout>
      <Head title="Dashboard" />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
            <p className="text-muted-foreground">
              Track your progress toward financial independence
            </p>
          </div>
        </div>

        {/* Demo Users Section - Show for new users with no data */}
        {demo_users && demo_users.length > 0 && (
          <Card className="border-2 border-blue-200 bg-blue-50">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-blue-600" />
                <CardTitle className="text-blue-900">
                  Try Demo Data First
                </CardTitle>
              </div>
              <p className="text-muted-foreground mt-2 text-sm">
                Explore the app with pre-populated sample data. You can see
                example portfolios, expenses, and goals.
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                {demo_users.map((demoUser) => (
                  <div
                    key={demoUser.id}
                    className="space-y-3 rounded-lg border border-blue-200 bg-white p-4"
                  >
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {demoUser.name}
                      </h3>
                      <p className="text-muted-foreground text-sm">
                        {demoUser.email}
                      </p>
                    </div>

                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Portfolios:
                        </span>
                        <span className="font-medium">
                          {demoUser.portfolios_count}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Expenses:</span>
                        <span className="font-medium">
                          {demoUser.expenses_count}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Goals:</span>
                        <span className="font-medium">
                          {demoUser.goals_count}
                        </span>
                      </div>
                      <div className="flex justify-between border-t border-gray-200 pt-2">
                        <span className="text-muted-foreground">
                          Portfolio Value:
                        </span>
                        <span className="font-semibold text-blue-600">
                          {formatCurrency(demoUser.total_portfolio_value)}
                        </span>
                      </div>
                    </div>

                    <Button
                      onClick={() => handleAssumeUser(demoUser.id)}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      size="sm"
                    >
                      <Zap className="mr-2 h-4 w-4" />
                      View This Demo
                    </Button>
                  </div>
                ))}
              </div>
              <p className="text-muted-foreground mt-4 text-xs">
                💡 Tip: Sign out to return to your actual account. Demo data is
                separate from your real data.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Key Metrics */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="flex items-center gap-1.5 text-sm font-medium">
                FIRE Number
                <InfoTooltip text="How much you'd need invested to live off your portfolio forever. Calculated as your annual expenses × 25 (the '4% rule')." />
              </CardTitle>
              <Target className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(fi_metrics.fi_number)}
              </div>
              <p className="text-muted-foreground text-xs">
                {formatCurrency(fi_metrics.annual_expenses)} annual expenses
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="flex items-center gap-1.5 text-sm font-medium">
                {fi_metrics.total_liabilities > 0
                  ? "Net Worth"
                  : "Portfolio Value"}
                <InfoTooltip
                  text={
                    fi_metrics.total_liabilities > 0
                      ? "What you own (investments) minus what you owe (debt)."
                      : "The current total value of everything in your investment portfolios."
                  }
                />
              </CardTitle>
              <DollarSign className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(
                  fi_metrics.total_liabilities > 0
                    ? fi_metrics.net_worth
                    : totalPortfolioValue,
                )}
              </div>
              <p className="text-muted-foreground text-xs">
                {fi_metrics.total_liabilities > 0
                  ? `${formatCurrency(totalPortfolioValue)} assets − ${formatCurrency(fi_metrics.total_liabilities)} debt`
                  : `${formatCurrency(totalPortfolioReturn)} total return`}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="flex items-center gap-1.5 text-sm font-medium">
                FI Progress
                <InfoTooltip text="How close you are to full financial independence: your portfolio value divided by your FIRE number." />
              </CardTitle>
              <TrendingUp className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {Number(fi_metrics.fi_progress_percentage).toFixed(1)}%
              </div>
              <Progress
                value={fi_metrics.fi_progress_percentage}
                className="mt-2"
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Active Goals
              </CardTitle>
              <BarChart3 className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{active_goals.length}</div>
              <p className="text-muted-foreground text-xs">
                Financial goals in progress
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Content Grid */}
        <div className="grid gap-6 md:grid-cols-2">
          {/* Recent Expenses */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Recent Expenses</CardTitle>
              <Button asChild variant="outline" size="sm">
                <Link href="/expenses">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Expense
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              {recent_expenses.length > 0 ? (
                <div className="space-y-3">
                  {recent_expenses.map((expense) => (
                    <div
                      key={expense.id}
                      className="flex items-center justify-between"
                    >
                      <div>
                        <div className="font-medium">
                          {expense.description || expense.category}
                        </div>
                        <div className="text-muted-foreground text-sm">
                          {expense.category} •{" "}
                          {formatDate(expense.expense_date)}
                        </div>
                      </div>
                      <div className="font-medium">
                        {formatCurrency(expense.amount)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center">
                  <p className="text-muted-foreground">No recent expenses</p>
                  <Button asChild variant="outline" size="sm" className="mt-2">
                    <Link href="/expenses/new">Add your first expense</Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Active Goals */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Active Goals</CardTitle>
              <Button asChild variant="outline" size="sm">
                <Link href="/goals">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Goal
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              {active_goals.length > 0 ? (
                <div className="space-y-4">
                  {active_goals.map((goal) => (
                    <div key={goal.id} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="font-medium">
                          {goal.goal_type_human}
                        </div>
                        <div className="text-muted-foreground text-sm">
                          {goal.progress_percentage.toFixed(1)}%
                        </div>
                      </div>
                      <Progress
                        value={goal.progress_percentage}
                        className="h-2"
                      />
                      <div className="text-muted-foreground flex justify-between text-sm">
                        <span>{formatCurrency(goal.current_amount)}</span>
                        <span>{formatCurrency(goal.target_amount)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center">
                  <p className="text-muted-foreground">No active goals</p>
                  <Button asChild variant="outline" size="sm" className="mt-2">
                    <Link href="/goals/new">Set your first goal</Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Portfolio Summary */}
        {portfolio_summary.length > 0 && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Portfolio Summary</CardTitle>
              <Button asChild variant="outline" size="sm">
                <Link href="/portfolios">
                  <BarChart3 className="mr-2 h-4 w-4" />
                  View All
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {portfolio_summary.map((portfolio) => (
                  <div
                    key={portfolio.id}
                    className="space-y-2 rounded-lg border p-4"
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-medium">{portfolio.name}</div>
                      <Badge
                        className={
                          getReturnColor(portfolio.total_return_percentage) ===
                          "text-green-600"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }
                      >
                        {portfolio.total_return_percentage >= 0 ? "+" : ""}
                        {Number(portfolio.total_return_percentage).toFixed(2)}%
                      </Badge>
                    </div>
                    <div className="text-muted-foreground text-sm">
                      {portfolio.portfolio_positions.length} positions
                    </div>
                    <div className="text-lg font-semibold">
                      {formatCurrency(portfolio.total_value)}
                    </div>
                    <Button
                      asChild
                      variant="outline"
                      size="sm"
                      className="w-full"
                    >
                      <Link href={`/portfolios/${portfolio.id}`}>
                        View Details
                      </Link>
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Button asChild variant="outline" className="h-20 flex-col">
                <Link href="/fi_calculator">
                  <Target className="mb-2 h-6 w-6" />
                  FIRE Calculator
                </Link>
              </Button>
              <Button asChild variant="outline" className="h-20 flex-col">
                <Link href="/expenses/new">
                  <Plus className="mb-2 h-6 w-6" />
                  Add Expense
                </Link>
              </Button>
              <Button asChild variant="outline" className="h-20 flex-col">
                <Link href="/goals/new">
                  <Target className="mb-2 h-6 w-6" />
                  Set Goal
                </Link>
              </Button>
              <Button asChild variant="outline" className="h-20 flex-col">
                <Link href="/portfolios/new">
                  <BarChart3 className="mb-2 h-6 w-6" />
                  New Portfolio
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
